/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Typography, Paper, Slide, LinearProgress, ToggleButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { MapboxOverlay } from '@deck.gl/mapbox';
import produce from 'immer';
import moment from 'moment-timezone';
import { useParams } from 'react-router-dom';
import { GeoJsonLayer, IconLayer, TextLayer } from '@deck.gl/layers';
import { WebMercatorViewport } from '@deck.gl/core';

// lasso

import bbox from '@turf/bbox';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import centerOfMass from '@turf/center-of-mass';
import { multiPolygon, point } from '@turf/helpers';
import Lasso from './Lasso';

import { Map as MapGL, Popup, useControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import MARKERS from '../../assets/dots.svg';
import { COLOR_PALETE } from '../../constants/color';
import { getMap } from '../../repositories/HttpRepository';
import Info from './Info';
import MapTools from './MapTools';

const mapStyle = `${process.env.REACT_APP_MAP_TILER_URI}?key=${process.env.REACT_APP_MAP_TILER_KEY}`;
const iconMapping = {};
const iconW = 36;
const iconH = 36;
const iconAnchor = 18;
iconMapping['blank'] = {
  x: 0,
  y: 0,
  width: iconW,
  height: iconH,
  anchorY: iconAnchor,
  mask: true,
};

for (let i = 0; i < COLOR_PALETE.length; i++) {
  iconMapping[`icon-${i}`] = {
    x: i * iconW,
    y: 0,
    width: iconW,
    height: iconH,
    anchorY: iconAnchor,
  };
}

/**
 *
 * @param {shipments} data
 */
const calculateBoundary = (data) => {
  let minLat, maxLat, minLng, maxLng;
  for (let i = 0; i < data.length; i++) {
    const sLat = data[i].latitude || data[i].dropoff_address.lat;
    const sLng = data[i].longitude || data[i].dropoff_address.lng;
    if (!minLat || minLat > sLat) {
      minLat = sLat;
    }

    if (!maxLat || maxLat < sLat) {
      maxLat = sLat;
    }

    if (!minLng || minLng > sLng) {
      minLng = sLng;
    }

    if (!maxLng || maxLng < sLng) {
      maxLng = sLng;
    }
  }

  let boundary = [
    [minLng, minLat],
    [maxLng, maxLat],
  ];

  if (boundary[1][0] < boundary[0][0] + 0.001) {
    boundary[1][0] = boundary[0][0] + 0.001;
  }
  if (boundary[1][1] < boundary[0][1] + 0.001) {
    boundary[1][1] = boundary[0][1] + 0.001;
  }

  return boundary;
};

export const fitBoundPolygon = (features, viewport, setViewport) => {
  if (features && features.length > 0) {
    const featuresCollection = {
      type: 'FeatureCollection',
      features: features,
    };
    const bboxResult = bbox(featuresCollection);
    const [minLng, minLat, maxLng, maxLat] = bboxResult; // Turf.js
    const viewportWeb = new WebMercatorViewport(viewport);
    const { longitude, latitude, zoom } = viewportWeb.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      {
        padding: 100,
      },
    );
    setViewport({
      ...viewport,
      longitude,
      latitude,
      zoom,
      transitionDuration: 500,
    });
  }
};

const useStyles = makeStyles()((theme) => ({
  toggle: {
    marginLeft: 'auto',
  },
  loading: {
    width: '100%',
  },
  root: {
    width: '100%',
    position: 'relative',
  },
  labelChip: {
    marginRight: '10px',
  },
  paper: {
    zIndex: 1,
    position: 'absolute',
    top: 0,
    right: 0,
    padding: 5,
  },
  btnWrapper: {
    marginBottom: 5,
    display: 'flex',
  },
  mapWrapper: {
    position: 'relative',
  },
  nameInput: {
    marginTop: 10,
    width: '100%',
    fontSize: '14px',
  },
}));

function DeckGLOverlay(props) {
  const overlay = useControl(() => new MapboxOverlay(props));
  overlay.setProps(props);
  return null;
}

const Map = React.forwardRef(({ timeZone, isLoadingMap, shipments, clientSelected, clientOption }, ref) => {
  const { classes } = useStyles();
  const { id } = useParams();
  const [nameValue, setNameValue] = useState('');
  const [noteValue, setNoteValue] = useState('');
  const [isOpenRight, setIsOpenRight] = useState(false);
  const [selected, setSelected] = useState(false);
  const [openDialogName, setOpenDialogName] = useState(false);

  const [layers, setLayers] = useState([]);
  const [viewport, setViewport] = useState({
    latitude: 33.851029206367905,
    longitude: -118.38935852050781,
    zoom: 11,
    width: 800,
    height: 700,
  });
  const [shipmentPopup, setShipmentPopup] = useState({});
  const [selectedShipment, setSelectedShipment] = useState([]);

  // for save/share geojson //list features
  const [geojson, setGeojson] = useState([]);
  const [lasso, setLasso] = useState(false);

  const openShipmentPopup = (info) => {
    if (info?.object?.shipment) {
      setShipmentPopup(info.object);
    } else {
      setShipmentPopup(null);
    }
  };

  const closeShipmentPopup = () => {
    setShipmentPopup(null);
  };

  const renderMarkers = (shipmentsInput) => {
    const markers = shipmentsInput.map((shipment) => {
      const clientIdx = clientSelected.findIndex((c) => c === shipment.client_id) + 1;
      const colorIdx = clientIdx % COLOR_PALETE.length;

      return {
        location: [shipment.longitude || shipment.dropoff_address.lng, shipment.latitude || shipment.dropoff_address.lat],
        shipment,
        size: 12,
        icon: `icon-${colorIdx}`,
      };
    });
    const dropoffLayer = new IconLayer({
      id: 'dropoff-icon-layer',
      data: markers,
      pickable: true,
      iconAtlas: MARKERS,
      iconMapping: iconMapping,
      sizeScale: 1,
      opacity: 1,
      visible: true,
      billboard: false,
      getIcon: (d) => d.icon,
      getPosition: (d) => d.location,
      getSize: (d) => d.size,
      onClick: openShipmentPopup,
    });
    const index = layers.findIndex((l) => l.id === 'dropoff-icon-layer');
    const newLayers = produce(layers, (draft) => {
      if (index > -1) {
        draft[index] = dropoffLayer;
      } else {
        draft.push(dropoffLayer);
      }
    });
    setLayers(newLayers);
  };

  const fitBoundary = (boundary) => {
    const isTooSmall = boundary[1][0] - boundary[0][0] < 0.0004;
    const c = {
      longitude: boundary[0][1],
      latitude: boundary[0][0],
      zoom: 18,
    };
    const viewportWeb = new WebMercatorViewport(viewport);
    boundary = boundary.slice().map((b) => b.slice());
    const renderViewport = viewportWeb.fitBounds(boundary, {
      padding: 60,
      offset: [0, -100],
    });

    const { longitude, latitude, zoom } = isTooSmall ? c : renderViewport;
    setViewport({
      ...viewport,
      longitude,
      latitude,
      zoom,
      transitionDuration: 500,
    });
  };

  const updateLasso = (data) => {
    if (shipments && shipments.length > 0) {
      const mPolygons = multiPolygon(data.features.map((f) => f.geometry.coordinates));
      const selectedShipments = shipments.filter((s) => {
        const p = point([
          s.longitude || s.dropoff_address.lng,
          s.latitude || s.dropoff_address.lat,
        ]);
        return booleanPointInPolygon(p, mPolygons);
      });
      if (selectedShipments && selectedShipments.length > 0) {
        setSelectedShipment(selectedShipments);
        setIsOpenRight(true);
        setSelected(true);
      }
    }
    if (data.features && data.features.length > 0) {
      const center = centerOfMass(data.features[0]);
      const newGeojson = {
        ...data.features[0],
        center: center.geometry.coordinates,
      };
      setGeojson(
        produce(geojson, (draft) => {
          draft.push(newGeojson);
        }),
      );
      setLasso(false);
    }
  };

  useEffect(() => {
    if (id) {
      getMap(id).then((resp) => {
        if (resp.data && resp.data.length > 0) {
          const { features } = resp.data[0];
          setGeojson(features);
        }
      });
    } else {
      setGeojson([]);
    }
  }, [id]);

  useEffect(() => {
    if (shipments && shipments.length > 0) {
      renderMarkers(shipments);
      fitBoundary(calculateBoundary(shipments));
    }
  }, [shipments]);

  useEffect(() => {
    if (geojson.length > 0) {
      const polygonLayer = new GeoJsonLayer({
        id: 'polygon-layer',
        data: geojson,
        opacity: 0.5,
        pickable: true,
        stroked: false,
        filled: true,
        extruded: true,
        lineWidthScale: 20,
        lineWidthMinPixels: 2,
        getFillColor: [160, 160, 160, 150],
        getRadius: 100,
        getLineWidth: 1,
        getElevation: 1,
        onClick: (event) => {
          onGeoJsonClick(event);
          return true;
        },
      });
      const centerTextsLayer = new TextLayer({
        id: 'center-texts-layer',
        data: geojson,
        pickable: true,
        getPosition: (d) => d.center,
        getText: (d) => d.properties.name || '',
        getColor: (d) => [127, 0, 255],
        getSize: (d) => 18,
        getAngle: 0,
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'center',
      });
      const poIndex = layers.findIndex((l) => l.id === 'polygon-layer');
      const textIndex = layers.findIndex((l) => l.id === 'center-texts-layer');
      const newLayers = produce(layers, (draft) => {
        if (poIndex > -1) {
          draft[poIndex] = polygonLayer;
        } else {
          draft.push(polygonLayer);
        }
        if (textIndex > -1) {
          draft[textIndex] = centerTextsLayer;
        } else {
          draft.push(centerTextsLayer);
        }
      });
      setLayers(newLayers);
      fitBoundPolygon(geojson, viewport, setViewport);
    } else {
      setLayers(
        produce(layers, (draft) => {
          const poIndex = layers.findIndex((l) => l.id === 'polygon-layer');
          const textIndex = layers.findIndex((l) => l.id === 'center-texts-layer');
          if (poIndex > -1) {
            draft.splice(poIndex, 1);
          }
          if (textIndex > -1) {
            draft.splice(textIndex, 1);
          }
        }),
      );
    }
  }, [geojson]);

  const handleKeyUp = (e) => {
    if (e.key === 's' || e.key === 'S') {
      setLasso(true);
      return;
    }
    if (e.which === 27) {
      setLasso(false);
    }
  };
  const onGeoJsonClick = (e) => {
    if (e?.object?.properties.name) {
      setNameValue(e.object.properties.name);
    } else {
      setNameValue('');
    }
    setNoteValue(e.object.properties.note || '');
    setOpenDialogName(e);
  };
  const saveProperty = () => {
    if (openDialogName) {
      const index = openDialogName.index;
      if (index > -1 && nameValue) {
        setGeojson(
          produce(geojson, (draft) => {
            draft[index].properties.name = nameValue;
            draft[index].properties.note = noteValue || '';
          }),
        );
        setNameValue('');
        setNoteValue('');
        setOpenDialogName(false);
        setLasso(false);
      }
    }
  };

  const removeGeoJson = () => {
    if (openDialogName) {
      // console.log('indopenDialogNameex', openDialogName);
      // console.log('layers', layers);
      // console.log('geojson', geojson);
      const { index } = openDialogName;
      if (index > -1 && geojson.length >= index) {
        setGeojson(
          produce(geojson, (draft) => {
            draft.splice(index, 1);
          }),
        );
      }
      setOpenDialogName(false);
    }
  };
  return (
    <div className={classes.root} ref={ref} onKeyUp={handleKeyUp}>
      <div className={classes.btnWrapper}>
        <MapTools geojson={geojson} selectedId={id} />
        <ToggleButton
          className={classes.toggle}
          color="primary"
          size="small"
          value="check"
          selected={selected}
          onChange={() => {
            setSelected(!selected);
            setIsOpenRight(!isOpenRight);
          }}
        >
          <AssignmentReturnIcon />
        </ToggleButton>
      </div>
      <div className={classes.mapWrapper}>
        {isLoadingMap && <LinearProgress color="primary" className={classes.loading} />}
        <MapGL {...viewport} mapStyle={mapStyle} onMove={(e) => setViewport((prev) => ({ ...prev, ...e.viewState }))} style={{ width: '100%', height: '700px', border: '1px solid #ccc' }}>
          <DeckGLOverlay layers={layers} />
          {shipmentPopup?.shipment && (
            <Popup anchor="bottom" latitude={shipmentPopup.shipment.latitude} longitude={shipmentPopup.shipment.longitude} closeOnClick={false} offset={[0, -8]} onClose={closeShipmentPopup}>
              <div>
                <ul>
                  <li>
                    ID: <strong>{shipmentPopup.shipment.id}</strong>
                  </li>
                  <li>
                    Region: <strong>{shipmentPopup.shipment.region_code}</strong>
                  </li>
                  <li>
                    Timezone: <strong>{timeZone}</strong>
                  </li>
                  <li>
                    {`Time Window: `}
                    <strong>
                      {moment.tz(shipmentPopup.shipment.dropoff_earliest_ts, timeZone).format('MM/DD/YYYY, h:mm:ss a')}
                      {` - `}
                      {moment.tz(shipmentPopup.shipment.dropoff_latest_ts, timeZone).format('MM/DD/YYYY, h:mm:ss a')}
                    </strong>
                  </li>
                </ul>
              </div>
            </Popup>
          )}
        </MapGL>
        <Slide direction="left" in={isOpenRight}>
          <Paper elevation={4} className={classes.paper}>
            <Info shipments={selectedShipment} clientOption={clientOption} />
          </Paper>
        </Slide>
        {lasso && <Lasso changeData={updateLasso} changeViewport={setViewport} viewport={viewport} />}
        <Dialog open={openDialogName} onClose={() => setOpenDialogName(false)} aria-labelledby="form-dialog-title" maxWidth="sm">
          <DialogTitle id="form-dialog-title" className={classes.DialogTitle}>
            <Typography variant="h" className={classes.Title}>
              Update property
            </Typography>
          </DialogTitle>
          <DialogContent className={classes.DialogContent}>
            <TextField
              size="small"
              variant="outlined"
              label="Name"
              className={classes.nameInput}
              onChange={(e) => {
                setNameValue(e.target.value);
              }}
              value={nameValue}
            />
            <TextField
              size="small"
              variant="outlined"
              label="Note"
              className={classes.nameInput}
              multiline
              rows={3}
              onChange={(e) => {
                setNoteValue(e.target.value);
              }}
              value={noteValue}
            />
          </DialogContent>
          <DialogActions className={classes.DialogAction}>
            <IconButton className={classes.closeBtn} onClick={() => removeGeoJson()} size="large">
              <DeleteIcon />
            </IconButton>
            <Button variant="outlined" color="primary" onClick={() => saveProperty()}>
              Save
            </Button>
            <Button variant="outlined" color="primary" onClick={() => setOpenDialogName(false)}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
});
export default React.memo(Map);
